/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react"
import { message, Card, Spin } from "antd"
import { useNavigate } from "react-router"
import { TempLayout } from "../../layouts"
import HeTimesheetForm from "./components/HeTimesheetForm"
import { IHeTimesheetDto } from "@/types"
import { UploadFile } from "antd/lib/upload/interface"
import { Moment } from "moment"
import { useActivitiesStore, useEquipmentsStore, useHeTimesheetStore, useLocationsStore, useOperatorsStore, useUserStore } from "@/stores"

const getErrorMessage = (error: unknown): string => {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
  if (apiMessage) return apiMessage
  if (error instanceof Error) return error.message
  return "An unknown error occurred"
}

interface IHeTimesheetFormValues {
  vendor_name: string
  branch_name: string
  department_name: string
  job_location?: string
  activity_name: string
  hull_name: string
  hull_code: string
  operator_name: string
  operator_id?: string
  shift_type: "PAGI" | "MALAM"
  date_range: [Moment, Moment]
  hm_start: number
  hm_stop: number
  hm_total: number
  depth_meter_from?: number
  depth_meter_to?: number
  depth_meter_total?: number
  note?: string
  hm_start_photo: UploadFile[]
  hm_stop_photo: UploadFile[]
  unit_photo: UploadFile[]
  operator: UploadFile[]
}

const HeTimesheetsAdd = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const isAdmin = userData?.roles?.includes("ROLE_ADMIN")
  const { createHeTimesheet } = useHeTimesheetStore()
  const { fetchEquipments } = useEquipmentsStore()
  const { fetchLocations } = useLocationsStore()
  const { fetchActivities } = useActivitiesStore()
  const { fetchOperators } = useOperatorsStore()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    void fetchEquipments({ page: 1, limit: 100 })
    void fetchLocations({ page: 1, limit: 100 })
    void fetchActivities({ page: 1, limit: 100 })
    void fetchOperators({ page: 1, limit: 100 })
  }, [fetchEquipments, fetchLocations, fetchActivities, fetchOperators])

  const handleSubmit = async (values: IHeTimesheetFormValues) => {
    setLoading(true)
    try {
      const getFile = (fileField: UploadFile[]) => {
        if (fileField && fileField.length > 0) {
          return fileField[0].originFileObj as File
        }
        return null
      }

      const hmStartPhoto = getFile(values.hm_start_photo)
      const hmStopPhoto = getFile(values.hm_stop_photo)
      const unitPhoto = getFile(values.unit_photo)
      const operatorPhoto = getFile(values.operator)

      if (!unitPhoto || (!isAdmin && (!hmStartPhoto || !operatorPhoto))) {
        void message.error(isAdmin ? "Please upload attachment" : "Please upload all required photos (HM Start, HM Stop, Operator Selfie)")
        setLoading(false)
        return
      }
      const [startDate, endDate] = values.date_range || []
      if (!startDate || !endDate) {
        void message.error("Please select date range")
        setLoading(false)
        return
      }

      const payload: IHeTimesheetDto = {
        vendor_name: values.vendor_name,
        branch_name: values.branch_name,
        department_name: values.department_name,
        job_location: values.job_location || "",
        hull_name: values.hull_name,
        hull_code: values.hull_code || "",
        operator_name: values.operator_name,
        operator_id: values.operator_id || "",
        depth_meter_from: values?.depth_meter_from || 0,
        depth_meter_to: values?.depth_meter_to || 0,
        depth_meter_total: values?.depth_meter_total || 0,
        shift_type: values.shift_type,
        date_from: startDate.toISOString(),
        date_to: endDate.toISOString(),
        hm_start: values.hm_start,
        hm_stop: values.hm_stop,
        hm_total: values.hm_total,
        note: values.note || "",
        activity_name: values.activity_name,
        hm_start_photo: hmStartPhoto,
        hm_stop_photo: hmStopPhoto,
        unit_photo: unitPhoto,
        operator: operatorPhoto,
      }

      await createHeTimesheet(payload)

      void message.success("He Timesheet created successfully!")
      navigate(-1) // Go back
    } catch (err: unknown) {
      void message.error(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <TempLayout title="HE Timesheet - Create New Entry" navNavigateUrl="/hets/history">
      <Card>
        <Spin spinning={loading}>
          <HeTimesheetForm isEditMode={false} loading={loading} onFinish={(values: any) => void handleSubmit(values as IHeTimesheetFormValues)} />
        </Spin>
      </Card>
    </TempLayout>
  )
}

export default HeTimesheetsAdd
